import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkflowService } from '../../../../data-access/workflow.service';
import {
  CommitteeMemberData,
  DATE_GATED_WORKFLOW_TYPES,
  DocumentSlotData,
  FormFieldSchema,
  WorkflowAttachment,
  WorkflowTaskDetail,
  WorkflowTaskStatus
} from '../../../../domain/types/workflow';
import { extractDateFromUtcString, extractErrorMessage, formatYear, getCaseStatusClass, getStageName, getTaskStatusClass, getWorkflowDisplayName } from '../workflow-utils';

@Component({
  selector: 'app-task-form',
  templateUrl: './task-form.component.html',
  styleUrls: ['./task-form.component.scss']
})
export class TaskFormComponent implements OnInit {
  taskId: number;
  task: WorkflowTaskDetail | null = null;
  form: FormGroup;
  loading = true;
  submitting = false;
  error: string | null = null;

  getStageName = getStageName;
  getWorkflowDisplayName = getWorkflowDisplayName;
  formatYear = formatYear;
  getTaskStatusClass = getTaskStatusClass;
  getCaseStatusClass = getCaseStatusClass;
  extractDateFromUtcString = extractDateFromUtcString;

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private workflowSvc: WorkflowService
  ) {
    this.taskId = +this.route.snapshot.params['taskId'];
  }

  async ngOnInit() {
    try {
      this.task = await this.workflowSvc.getTask(this.taskId);
      this.buildForm();
    } catch (err: any) {
      this.error = err?.status === 403 ? extractErrorMessage(err, 'Access denied') : 'Failed to load task';
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  get positionDisplay(): string | null {
    if (!this.task) return null;
    if (!['Promotion', 'Merit', 'FiveYearReview'].includes(this.task.workflowType)) return null;
    const from = this.task.currentPositionLabel;
    const to = this.task.proposedPositionLabel;
    if (from && to && from !== to) return `${from} \u2192 ${to}`;
    if (to) return `\u2192 ${to}`;
    return null;
  }

  navigateToPacket() {
    if (this.task) {
      this.router.navigate(['/review/packet', this.task.caseId]);
    }
  }

  get isReadOnly(): boolean {
    if (this.task?.status === WorkflowTaskStatus.Skipped) return true;
    // Prefer the server's editability verdict — it knows about admin bypass,
    // date gates, and the candidate-can-revise-while-open exception.
    if (typeof this.task?.editable === 'boolean') return !this.task.editable;
    return this.task?.status === WorkflowTaskStatus.Completed;
  }

  get isUpcoming(): boolean {
    return this.task?.gateStatus === 'Upcoming';
  }

  get isPastDue(): boolean {
    return this.task?.gateStatus === 'PastDue';
  }

  buildForm() {
    if (!this.task) return;

    const controls: Record<string, any> = {};
    for (const field of (this.task.formSchema || [])) {
      if (field.type === 'file' || field.type === 'documentList' || field.type === 'committeeMembers') continue;

      const validators = [];
      if (field.required) validators.push(Validators.required);
      if (field.validation?.minLength) validators.push(Validators.minLength(field.validation.minLength));
      if (field.validation?.maxLength) validators.push(Validators.maxLength(field.validation.maxLength));

      const existingValue = this.task.formData?.[field.name] ?? '';
      controls[field.name] = [existingValue, validators];
    }
    this.form = this.fb.group(controls);

    if (this.isReadOnly) {
      this.form.disable();
    }
  }

  getDocumentList(fieldName: string): DocumentSlotData[] {
    return this.task?.formData?.[fieldName] ?? [];
  }

  getCommitteeMembers(fieldName: string): CommitteeMemberData[] {
    return this.task?.formData?.[fieldName] ?? [];
  }

  getFieldError(field: FormFieldSchema): string | null {
    const control = this.form?.get(field.name);
    if (!control || !control.touched || !control.errors) return null;

    if (control.errors['required']) return `${field.label} is required`;
    if (control.errors['minlength']) return `Minimum ${field.validation?.minLength} characters`;
    if (control.errors['maxlength']) return `Maximum ${field.validation?.maxLength} characters`;
    return null;
  }

  get hasDocumentList(): boolean {
    return this.task?.formSchema.some(f => f.type === 'documentList') ?? false;
  }

  getAttachmentsForSlot(slotName: string): WorkflowAttachment[] {
    return this.task?.attachments.filter(a => a.documentSlot === slotName) ?? [];
  }

  async onFileUpload(event: Event, documentSlot: string) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    try {
      await this.workflowSvc.uploadAttachment(this.taskId, file, documentSlot);
      // Reload task to get updated attachments
      this.task = await this.workflowSvc.getTask(this.taskId);
    } catch (err: any) {
      const serverMessage = extractErrorMessage(err, 'Unknown error');
      alert(`Upload failed: ${serverMessage}`);
    }
    // Reset file input
    input.value = '';
  }

  async deleteAttachment(attachment: WorkflowAttachment) {
    if (!confirm(`Delete ${attachment.fileName}?`)) return;

    try {
      await this.workflowSvc.deleteAttachment(attachment.id);
      if (this.task) {
        this.task.attachments = this.task.attachments.filter(a => a.id !== attachment.id);
      }
    } catch (err) {
      console.error('Delete failed', err);
    }
  }

  viewAttachment(attachment: WorkflowAttachment) {
    this.workflowSvc.viewAttachment(attachment.id);
  }

  downloadAttachment(attachment: WorkflowAttachment) {
    this.workflowSvc.downloadAttachment(attachment.id, attachment.fileName);
  }

  get canRescind(): boolean {
    if (!this.task?.completedAt) return false;
    if (this.task.stage !== 'CandidateSubmission') return false;
    if (!DATE_GATED_WORKFLOW_TYPES.includes(this.task.workflowType)) return false;
    // Only while still revisable — completed + editable + window open.
    return !this.isReadOnly && !this.isPastDue;
  }

  async rescind() {
    if (!confirm('This will reset your submission and remove all submitted documents. Are you sure?')) return;
    try {
      await this.workflowSvc.rescindTask(this.taskId);
      this.task = await this.workflowSvc.getTask(this.taskId);
    } catch (err: any) {
      alert(`Rescind failed: ${extractErrorMessage(err, 'Unknown error')}`);
    }
  }

  async submit() {
    if (!this.form || this.form.invalid) {
      // Mark all fields as touched to show validation
      Object.values(this.form.controls).forEach(c => c.markAsTouched());
      return;
    }

    this.error = null;

    // Validate required document slots have at least one attachment
    const missing = this.getMissingRequiredSlots();
    if (missing.length > 0) {
      alert(`Please upload at least one file for:\n\n${missing.join('\n')}`);
      return;
    }

    this.submitting = true;
    try {
      await this.workflowSvc.completeTask(this.taskId, this.form.value);
      this.router.navigate(['/review/my-tasks']);
    } catch (err) {
      this.error = 'Failed to submit task';
      console.error(err);
    } finally {
      this.submitting = false;
    }
  }

  private getMissingRequiredSlots(): string[] {
    if (!this.task) return [];
    const missing: string[] = [];
    for (const field of this.task.formSchema) {
      if (field.type === 'documentList') {
        const docs: DocumentSlotData[] = this.task.formData?.[field.name] ?? [];
        for (const doc of docs) {
          if (doc.minCount > 0 && this.getAttachmentsForSlot(doc.name).length === 0) {
            missing.push(doc.name);
          }
        }
      } else if (field.type === 'file' && field.required && !this.hasDocumentList) {
        if (this.getAttachmentsForSlot(field.name).length === 0) {
          missing.push(field.label);
        }
      }
    }
    return missing;
  }

  cancel() {
    this.location.back();
  }
}
